import { useCallback, useEffect, useRef, useState } from "react";
import { Mic } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";

export default function VoiceBatch({ onDone }) {
  const [st, setSt] = useState(null);
  const done = useRef(onDone);
  done.current = onDone;
  const load = useCallback(() => api.get("/admin/voices/status").then((r) => setSt(r.data)), []);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    if (!st?.running) return;
    const t = setTimeout(async () => {
      const { data } = await api.get("/admin/voices/status");
      setSt(data);
      if (!data.running) {
        toast.success(`Voices ready: ${data.done} created${data.errors ? `, ${data.errors} failed` : ""}`);
        done.current?.();
      }
    }, 4000);
    return () => clearTimeout(t);
  }, [st]);
  const start = async (overwrite) => {
    if (overwrite && !window.confirm("Re-record every affirmation script and challenge day? This replaces existing audio.")) return;
    try {
      await api.post("/admin/voices/parent-child", { overwrite });
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  if (!st) return null;
  return (
    <div className="mb-6 flex flex-col gap-3 rounded-2xl bg-bt-sage p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="voice-batch">
      <p className="flex items-center gap-2 text-sm text-bt-heading">
        <Mic size={16} />
        {st.running ? `Recording voices... ${st.done || 0} of ${st.total}` : st.missing ? `Missing voice recordings: ${st.resources_missing} affirmation script${st.resources_missing === 1 ? "" : "s"}, ${st.days_missing} challenge day${st.days_missing === 1 ? "" : "s"}, ${st.weekly_missing || 0} weekly message${st.weekly_missing === 1 ? "" : "s"}` : "Every affirmation script, challenge day and weekly message has a voice recording."}
      </p>
      {!st.running && (
        <div className="flex gap-2">
          {st.missing > 0 && <button onClick={() => start(false)} className="btn-primary !min-h-[40px] text-sm" data-testid="voice-batch-missing">Record missing</button>}
          <button onClick={() => start(true)} className="btn-ghost text-sm" data-testid="voice-batch-all">Re-record all</button>
        </div>
      )}
    </div>
  );
}
